import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Panel, PageHeader, StatusBadge } from "@/components/Widgets";
import { errMsg, http } from "@/lib/api";
import CompareVersions from "@/pages/CompareVersions";
import MappingConfig from "@/pages/MappingConfig";

const Uploader = () => {
  const nav = useNavigate();
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const upload = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    fd.append("notes", notes);
    setBusy(true);
    try {
      const r = await http.post("/versions/upload", fd);
      toast.success("Workbook parsed — review the mapping");
      nav(`/data/review/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Upload a new workbook version" subtitle="Parsed by sheet names, row labels and FY headers — you confirm any changes before the dashboard updates" testid="upload-panel">
      <div
        data-testid="excel-upload-dropzone"
        onClick={() => ref.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files[0]); }}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          drag ? "border-amber-500 bg-amber-500/5" : "border-slate-700 hover:border-slate-500"}`}
      >
        <UploadCloud className="h-8 w-8 text-amber-400" strokeWidth={1.5} />
        <div className="mt-3 text-sm text-slate-200">{file ? file.name : "Drop the Group Cash Flow .xlsx here or click to browse"}</div>
        <div className="mt-1 text-xs text-slate-500">Save the workbook in Excel first so formula results are included</div>
        <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="excel-upload-input" onChange={(e) => setFile(e.target.files[0])} />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Version name (e.g. Group CF Sep-26 - R7)" className="max-w-sm border-slate-700 bg-slate-900/70" data-testid="upload-version-name-input" />
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Management notes (optional)" className="max-w-md border-slate-700 bg-slate-900/70" data-testid="upload-notes-input" />
        <Button disabled={!file || busy} onClick={upload} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="upload-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Parse & preview mapping
        </Button>
      </div>
    </Panel>
  );
};

const VersionRow = ({ v }) => {
  const { activate, refresh } = useData();
  const nav = useNavigate();
  const [confirmDel, setConfirmDel] = useState(false);
  const { can } = useAuth();
  const editor = can("admin", "modeller");
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-slate-800" data-testid={`version-row-${v.id}`}>
      <td className="py-3 pr-3"><div className="text-slate-100">{v.name}</div><div className="text-[11px] text-slate-500">{v.filename}{v.uploaded_by ? ` · ${v.uploaded_by}` : ""}</div>{v.notes && <div className="mt-0.5 text-[11px] italic text-slate-400" data-testid={`version-notes-${v.id}`}>{v.notes}</div>}</td>
      <td className="num py-3 pr-3 text-xs text-slate-400">{new Date(v.uploaded_at).toLocaleString()}</td>
      <td className="py-3 pr-3">
        <StatusBadge ok={v.status === "confirmed"}>{v.status === "confirmed" ? "Confirmed" : "Pending review"}</StatusBadge>
        {v.is_active && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-400" data-testid="version-active-badge">Active</span>}
      </td>
      <td className="num py-3 pr-3 text-xs text-slate-400">{v.summary?.entities} entities · {v.summary?.items} items · {v.summary?.new} new</td>
      <td className="py-3 text-right">
        <div className="flex justify-end gap-2">
          {editor && v.status === "pending" && <Button size="sm" className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid={`version-review-${v.id}`} onClick={() => nav(`/data/review/${v.id}`)}>Review</Button>}
          {editor && v.status === "confirmed" && !v.is_active && <Button size="sm" variant="outline" className="border-slate-700" data-testid={`version-activate-${v.id}`} onClick={() => act(() => activate(v.id), "Version activated")}>Activate</Button>}
          {v.status === "confirmed" && <Button size="sm" variant="ghost" data-testid={`version-mapping-${v.id}`} onClick={() => nav(`/data/review/${v.id}`)}>Mapping</Button>}
          {editor && !v.is_active && (
            <Button size="sm" variant="ghost" className={confirmDel ? "text-rose-400" : "text-slate-500"} data-testid={`version-delete-${v.id}`}
              onClick={() => (confirmDel ? act(async () => { await http.delete(`/versions/${v.id}`); await refresh(); }, "Version deleted") : setConfirmDel(true))}>
              {confirmDel ? "Confirm delete" : "Delete"}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default function DataVersions() {
  const { versions } = useData();
  const { can } = useAuth();
  return (
    <div data-testid="data-page">
      <PageHeader kicker="Workbook ingestion" title="Data & Versions" />
      <Tabs defaultValue="versions">
        <TabsList className="mb-4 bg-slate-900/70">
          <TabsTrigger value="versions" data-testid="data-tab-versions">Versions</TabsTrigger>
          <TabsTrigger value="compare" data-testid="data-tab-compare">Compare</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="data-tab-mapping">Mapping rules</TabsTrigger>
        </TabsList>
        <TabsContent value="versions" className="space-y-6">
          {can("admin", "modeller") ? <Uploader /> : <Panel title="Read-only access" testid="viewer-readonly-note"><p className="text-sm text-slate-400">Leadership viewers can browse every version. Uploads and confirmations are done by administrators and financial modellers.</p></Panel>}
          <Panel title="Version history" testid="versions-table-panel">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <tbody>{versions.map((v) => <VersionRow key={v.id} v={v} />)}</tbody>
              </table>
            </div>
          </Panel>
        </TabsContent>
        <TabsContent value="compare"><CompareVersions /></TabsContent>
        <TabsContent value="mapping"><MappingConfig /></TabsContent>
      </Tabs>
    </div>
  );
}
